import React, { useState, useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { getEventImageUrl } from '../utils/eventImage';

export default function EventItems() {
  const { strings } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_EVENTS_URL);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();

        // Safely resolve data array whether response is an array or nested object
        const rawEvents = Array.isArray(data)
          ? data
          : data.events || data.data || [];

        if (!Array.isArray(rawEvents)) {
          throw new Error('Unexpected data format received from API');
        }

        // Sort events chronologically by start time
        const sortedEvents = [...rawEvents].sort((a, b) => {
          const timeA = new Date(a.startTime || a.start_time).getTime();
          const timeB = new Date(b.startTime || b.start_time).getTime();
          return timeA - timeB;
        });

        setEvents(sortedEvents);
      } catch (err) {
        setError(err.message || 'Error loading events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const formatDate = (startDateStr, endDateStr) => {
    if (!startDateStr) return { day: '--', month: '---', timeStr: '--:--' };
    const startDate = new Date(startDateStr);
    const day = startDate.getDate().toString().padStart(2, '0');
    const month = startDate
      .toLocaleString(strings.lang_code === 'RU' ? 'ru' : 'en', { month: 'short' })
      .toUpperCase();
    
    const startTime = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    let timeStr = startTime;
    if (endDateStr) {
      const endDate = new Date(endDateStr);
      const endTime = endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      // If end date is on another day, include end date in format
      if (startDate.toDateString() !== endDate.toDateString()) {
        const endDay = endDate.getDate().toString().padStart(2, '0');
        const endMonth = endDate
          .toLocaleString(strings.lang_code === 'RU' ? 'ru' : 'en', { month: 'short' })
          .toUpperCase();
        timeStr = `${startTime} – ${endDay} ${endMonth} ${endTime}`;
      } else {
        timeStr = `${startTime} – ${endTime}`;
      }
    }

    return { day, month, timeStr };
  };

  if (loading) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0' }}>
        <p style={{ opacity: 0.7 }}>Loading events...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0' }}>
        <p style={{ color: '#e74c3c' }}>{error}</p>
      </div>
    );
  }

  const activeEvents = events.filter((event) => Number(event.active) === 1);

  if (activeEvents.length === 0) {
    return (
      <div className="content-wrapper" style={{ justifyContent: 'center', padding: '40px 0', textAlign: 'center' }}>
        <p style={{ opacity: 0.8, fontSize: '1.1rem' }}>
          {strings.pages?.events?.no_events || 'В настоящее время нет запланированных мероприятий. Пожалуйста, загляните позже.'}
        </p>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      {activeEvents.map((event) => {
        const startTime = event.startTime || event.start_time;
        const endTime = event.endTime || event.end_time;
        const memberPrice = event.memberPrice ?? event.member_price ?? '0';
        const nonMemberPrice = event.nonMemberPrice ?? event.non_member_price ?? '0';
        const paymentLink = event.paymentLink || event.payment_link;
        const capacity = event.capacity;
        const description = event.description;
        const imagePath = getEventImageUrl(event.imagePath || event.image_path || event.image);

        const { day, month, timeStr } = formatDate(startTime, endTime);

        return (
          <div key={event.id} className="service-card event" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              {/* Bold Number Date Header Component */}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '2.5rem', fontWeight: '800', color: '#d4af37', lineHeight: '1' }}>
                  {day}
                </span>
                <span style={{ fontSize: '0.9rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {month} • {timeStr}
                </span>
              </div>

              <h3
                className="service-name"
                style={{
                  fontSize: '20px',
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 400,
                  marginBottom: '12px',
                }}
              >
                {event.title}
              </h3>

              {description && (
                <p
                  className="event-description"
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.5',
                    marginBottom: '16px',
                    opacity: 0.9,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {description}
                </p>
              )}

              <ul className="feature-list" style={{ marginBottom: '24px' }}>
                {event.location && (
                  <li>
                    <strong>{strings.pages?.events?.location || 'Location'}:</strong> {event.location}
                  </li>
                )}
                {capacity !== null && capacity !== undefined && capacity !== '' && (
                  <li>
                    <strong>{strings.pages?.events?.capacity || 'Capacity'}:</strong> {capacity} {strings.pages?.events?.seats || 'seats'}
                  </li>
                )}
                <li>
                  <strong>{strings.pages?.events?.member_price || 'Member Price'}:</strong>{' '}
                  <span style={{ fontWeight: '700', color: '#d4af37' }}>
                    CHF {memberPrice}
                  </span>
                </li>
                <li>
                  <strong>{strings.pages?.events?.non_member_price || 'Non-Member Price'}:</strong>{' '}
                  <span style={{ fontWeight: '700' }}>
                    CHF {nonMemberPrice}
                  </span>
                </li>
              </ul>
            </div>

            {paymentLink ? (
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                style={{ width: '100%', justifyContent: 'center', textAlign: 'center', marginBottom: '16px' }}
              >
                {strings.lang_code === 'RU' ? 'Зарегистрироваться и оплатить' : (strings.pages?.events?.register || 'Register')}
              </a>
            ) : (
              <a
                href="#contact"
                className="btn-gold"
                style={{ width: '100%', justifyContent: 'center', textAlign: 'center', opacity: 0.8, marginBottom: '16px' }}
              >
                {strings.lang_code === 'RU' ? 'Подробнее' : (strings.pages?.events?.more_info || 'More Info')}
              </a>
            )}

            {imagePath && (
              <details open style={{ marginBottom: '16px' }}>
                <summary className="event-details-toggle">
                  {strings.lang_code === 'RU' ? 'Детали мероприятия' : 'Event Details'}
                </summary>
                <img
                  src={imagePath}
                  alt={`${event.title} poster`}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }}
                />
              </details>
            )}

            {paymentLink ? (
              <a
                href={paymentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold"
                style={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}
              >
                {strings.lang_code === 'RU' ? 'Зарегистрироваться и оплатить' : (strings.pages?.events?.register || 'Register')}
              </a>
            ) : (
              <a
                href="#contact"
                className="btn-gold"
                style={{ width: '100%', justifyContent: 'center', textAlign: 'center', opacity: 0.8 }}
              >
                {strings.lang_code === 'RU' ? 'Подробнее' : (strings.pages?.events?.more_info || 'More Info')}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}